/**
 * Financeiro → Visão geral (dashboard v2). [F2 / 017]
 *
 * Layout fiel à referência oficial de design:
 *   integracao-nova/financeiro/titam-crm-financeiro-dashboard-v2.png
 *
 * Estrutura em 4 faixas:
 *   1. 6 KPIs (saldo, a receber, a pagar, receita, margem, ticket médio)
 *   2. Fluxo de caixa (saldo realizado × projetado) | Receitas × despesas
 *   3. Despesas por categoria | Rentabilidade | Ticket médio
 *   4. Próximos vencimentos | Saldos em contas bancárias | Atenção
 *
 * Tudo vem de GET /api/financeiro/dashboard?from&to — uma requisição só,
 * agregada no banco (ver api/financeiro/repo.py). Todo o conteúdo respeita o
 * período do seletor, inclusive os gráficos, que cobrem o intervalo inteiro:
 * "Este ano" desenha Jan..Dez, com os meses à frente projetados. Períodos de
 * até dois meses vêm agregados por dia; acima disso, por mês.
 *
 * Exceção proposital: "Saldo disponível" é snapshot das contas bancárias, não
 * fluxo — não muda com o filtro. "A receber" e "A pagar" são o que ainda vai
 * vencer dentro do período e batem, título a título, com os cards das telas
 * Contas a Receber/Pagar (ver os tooltips dos cards).
 *
 * Estilos em financeiro-dashboard.css (prefixo `fd-`), porque a dashboard
 * depende de media queries para colapsar as colunas sem perder o visual.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  ResponsiveContainer, ComposedChart, Area, BarChart, Bar, LineChart, Line,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, ReferenceLine, Tooltip,
} from 'recharts'
import Layout from '../../components/Layout.jsx'
import Icon from '../../components/icons.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { getFinanceiroDashboard, fmtBRL } from '../../db/api.js'
import { fmtDate, daysUntil, TIPO_SALDO_LABEL, AccountLogo } from './financeiroKit.jsx'
import './financeiro-dashboard.css'

// ── Paleta do design (mesma da referência) ──────────────────────────────────
const BLUE = '#2563eb'
const INK = '#1e293b'
const GRID = '#f1f3f7'
const AXIS = '#98a2b3'
// cores do donut de despesas, na ordem das fatias
const CORES_CATEGORIA = [BLUE, '#334155', '#60a5fa', '#93c5fd', '#94a3b8', '#cbd5e1']

const MESES = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez']

// As séries vêm com chave 'YYYY-MM' (mensal) ou 'YYYY-MM-DD' (diária) conforme
// a granularidade que o backend escolheu para o período.
const ehDia = (k) => String(k).length === 10
// eixo X: 'Jan' ou '04/08'
const rotuloCurto = (k) => {
  const [y, m, d] = String(k).split('-')
  return ehDia(k) ? `${d}/${m}` : (MESES[Number(m) - 1] || k)
}
// tooltip e pontas do eixo: 'ago/26' ou '04/08/2026'
const rotuloLongo = (k) => {
  const [y, m, d] = String(k).split('-')
  return ehDia(k)
    ? `${d}/${m}/${y}`
    : `${(MESES[Number(m) - 1] || m).toLowerCase()}/${String(y).slice(2)}`
}
// mesma chave que o backend geraria para hoje, na granularidade da série
const chaveDeHoje = (gran) => {
  const h = new Date()
  const ym = `${h.getFullYear()}-${String(h.getMonth() + 1).padStart(2, '0')}`
  return gran === 'dia' ? `${ym}-${String(h.getDate()).padStart(2, '0')}` : ym
}

const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0)

// Contas importadas (Conexa/Hermes) trazem o dump da integração na descrição,
// com dezenas de linhas. Na tabela só interessa a primeira.
const primeiraLinha = (s) => String(s || '').split('\n')[0].trim()
// R$ sem centavos — o design mostra os KPIs arredondados (R$ 182.340)
const brl0 = (v) => `R$ ${Math.round(num(v)).toLocaleString('pt-BR')}`
const pct1 = (v) => `${num(v).toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`

// Eixos: escala automática, então o rótulo se adapta à ordem de grandeza.
const axisBRL = (v) => {
  const n = num(v)
  const abs = Math.abs(n)
  const sinal = n < 0 ? '-' : ''
  if (abs >= 1e6) return `${sinal}R$ ${(abs / 1e6).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}M`
  if (abs >= 1000) return `${sinal}R$ ${Math.round(abs / 1000).toLocaleString('pt-BR')}k`
  return `${sinal}R$ ${Math.round(abs)}`
}
const axisPct = (v) => `${Math.round(num(v))}%`

// ── Período ─────────────────────────────────────────────────────────────────
// Os presets fecham em datas de calendário: "Este mês" vai do dia 1 ao último
// dia do mês (e não até hoje) e "Este ano" vai de 1º/jan a 31/dez. Assim o
// gráfico desenha o intervalo inteiro, com o que vem depois de hoje projetado,
// em vez de cortar no dia de hoje.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
const primeiroDoMes = (d) => new Date(d.getFullYear(), d.getMonth(), 1)
const ultimoDoMes = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0)
// Semana de segunda a domingo (padrão brasileiro; getDay() manda 0 no domingo)
const segundaDaSemana = (d) => {
  const dias = (d.getDay() + 6) % 7
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() - dias)
}

const PRESETS = [
  { id: 'hoje', label: 'Hoje', range: (h) => ({ from: iso(h), to: iso(h) }) },
  {
    id: 'semana',
    label: 'Esta semana',
    range: (h) => {
      const seg = segundaDaSemana(h)
      const dom = new Date(seg.getFullYear(), seg.getMonth(), seg.getDate() + 6)
      return { from: iso(seg), to: iso(dom) }
    },
  },
  { id: 'mes', label: 'Este mês', range: (h) => ({ from: iso(primeiroDoMes(h)), to: iso(ultimoDoMes(h)) }) },
  { id: 'ano', label: 'Este ano', range: (h) => ({ from: `${h.getFullYear()}-01-01`, to: `${h.getFullYear()}-12-31` }) },
]
const PRESET_PADRAO = 'ano'
const acharPreset = (id) => PRESETS.find((p) => p.id === id)

/**
 * Seletor de período: os 4 presets de calendário + intervalo livre.
 *
 * O intervalo livre só é aplicado com as duas datas preenchidas e na ordem
 * certa; enquanto isso o período em vigor continua o anterior.
 */
function PeriodoSelect({ presetId, custom, onSelect }) {
  const [open, setOpen] = useState(false)
  const [mostrarDatas, setMostrarDatas] = useState(presetId === 'custom')
  const [rascunho, setRascunho] = useState(custom)
  const ref = useRef(null)

  useEffect(() => {
    const fora = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', fora)
    return () => document.removeEventListener('mousedown', fora)
  }, [])

  const rotulo = presetId === 'custom'
    ? `${fmtDate(custom.from)} – ${fmtDate(custom.to)}`
    : (acharPreset(presetId) || acharPreset(PRESET_PADRAO)).label

  const valido = rascunho.from && rascunho.to && rascunho.from <= rascunho.to

  const aplicar = () => {
    if (!valido) return
    onSelect('custom', rascunho)
    setOpen(false)
  }

  return (
    <div className="fd-period" ref={ref}>
      <button type="button" className="fd-tb-btn" onClick={() => setOpen((o) => !o)}>
        <Icon n="calendar" s={14} /> {rotulo} <Icon n="chevron_d" s={13} />
      </button>
      {open && (
        <div className="fd-period-menu">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              className={`fd-period-item${p.id === presetId ? ' active' : ''}`}
              onClick={() => { setMostrarDatas(false); onSelect(p.id); setOpen(false) }}
            >
              {p.label}
            </button>
          ))}

          <div className="fd-period-sep" />
          <button
            type="button"
            className={`fd-period-item${presetId === 'custom' ? ' active' : ''}`}
            onClick={() => setMostrarDatas((v) => !v)}
          >
            Selecionar período
          </button>

          {mostrarDatas && (
            <div className="fd-period-custom">
              <label>
                De
                <input type="date" value={rascunho.from}
                  max={rascunho.to || undefined}
                  onChange={(e) => setRascunho((r) => ({ ...r, from: e.target.value }))} />
              </label>
              <label>
                Até
                <input type="date" value={rascunho.to}
                  min={rascunho.from || undefined}
                  onChange={(e) => setRascunho((r) => ({ ...r, to: e.target.value }))} />
              </label>
              <button type="button" className="fd-tb-btn primary" disabled={!valido} onClick={aplicar}>
                Aplicar
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Peças visuais ───────────────────────────────────────────────────────────

/**
 * Variação vs. período anterior. A seta segue o movimento (subiu/desceu) e a
 * cor segue o significado: em "A pagar" e "Custos" cair é bom, então
 * `bomQuandoSobe={false}` inverte o verde/vermelho.
 * `valor` nulo = sem base de comparação; mostra um traço para não pular o
 * layout do card.
 */
function Delta({ valor, unidade = '%', bomQuandoSobe = true, suffix = 'vs. período anterior' }) {
  if (valor == null) {
    return <div className="fd-delta"><span className="fd-delta-rest">sem base de comparação</span></div>
  }
  const sobe = num(valor) >= 0
  const bom = sobe === bomQuandoSobe
  const abs = Math.abs(num(valor))
  // Base quase zero no período anterior gera variações de 4 dígitos (ex.: um
  // saldo que saiu de R$ 340 para R$ 2.300 = +577%). Acima de 999% o número
  // deixa de informar, então vira um teto.
  const texto = abs >= 1000
    ? '>999'
    : abs.toLocaleString('pt-BR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })
  return (
    <div className={`fd-delta ${bom ? 'up' : 'down'}`}>
      <Icon n={sobe ? 'arrow_up' : 'arrow_down'} s={13} sw={2} />
      <b>{texto}{unidade === 'pp' ? ' p.p.' : '%'}</b>
      {suffix && <span className="fd-delta-rest">{suffix}</span>}
    </div>
  )
}

// O ⓘ do design não é decorativo: carrega a definição do indicador no `title`.
const Info = ({ texto }) => (
  <span className="fd-info" title={texto}><Icon n="info" s={13} /></span>
)

/**
 * `nota` substitui a variação nos KPIs que são posição atual (A receber /
 * A pagar): não existe "a vencer do período anterior" para comparar, então no
 * lugar do percentual vai a contagem de títulos.
 */
function Kpi({ icon, label, value, delta, unidade, bomQuandoSobe = true, info, nota }) {
  return (
    <div className="fd-card fd-kpi">
      <div className="fd-kpi-head">
        <span className="fd-kpi-icon"><Icon n={icon} s={17} sw={1.7} /></span>
        <span className="fd-kpi-label">{label}</span>
        {info && <Info texto={info} />}
      </div>
      <div className="fd-kpi-value">{value}</div>
      {nota
        ? <div className="fd-delta"><span className="fd-delta-rest">{nota}</span></div>
        : <Delta valor={delta} unidade={unidade} bomQuandoSobe={bomQuandoSobe} />}
    </div>
  )
}

function PanelHead({ title, sub, info }) {
  return (
    <div className="fd-panel-head">
      <div className="fd-panel-title">{title}{sub && <span> {sub}</span>}</div>
      {info && <span className="fd-info" title={info}><Icon n="info" s={15} /></span>}
    </div>
  )
}

// Pílula "Atual" sobre a linha vertical que separa realizado de projetado.
function AtualTag({ viewBox }) {
  const x = viewBox?.x ?? 0
  const y = viewBox?.y ?? 0
  return (
    <g transform={`translate(${x - 20}, ${y - 20})`}>
      <rect width={40} height={17} rx={5} fill={BLUE} />
      <text x={20} y={12} textAnchor="middle" fontSize={10.5} fontWeight="600" fill="#fff">Atual</text>
    </g>
  )
}

// A logo das contas bancárias (AccountLogo) vive no financeiroKit: a tela de
// Saldo mostra as mesmas contas, com o mesmo quadrado.

const tickStyle = { fontSize: 11, fill: AXIS }
const tooltipStyle = { borderRadius: 10, border: '1px solid #eaecf0', fontSize: 12 }

// Painel sem dados no período: mantém a altura do card para o grid não pular.
function SemDados({ altura = 150, texto = 'Sem dados no período.' }) {
  return (
    <div className="fd-empty" style={{ height: altura }}>{texto}</div>
  )
}

// ── Página ──────────────────────────────────────────────────────────────────
export default function FinanceiroDashboard() {
  const { selectedClient } = useApp()
  const clientId = selectedClient?.id || null

  const [presetId, setPresetId] = useState(PRESET_PADRAO)
  // intervalo livre; só entra em uso quando presetId === 'custom'
  const [custom, setCustom] = useState(() => acharPreset(PRESET_PADRAO).range(new Date()))
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState(null)

  const periodo = useMemo(() => {
    if (presetId === 'custom') return custom
    return (acharPreset(presetId) || acharPreset(PRESET_PADRAO)).range(new Date())
  }, [presetId, custom])

  const escolherPeriodo = useCallback((id, intervalo) => {
    if (id === 'custom' && intervalo) setCustom(intervalo)
    setPresetId(id)
  }, [])

  const load = useCallback(async () => {
    if (!clientId) { setData(null); setLoading(false); return }
    setLoading(true); setErr(null)
    try { setData(await getFinanceiroDashboard({ ...periodo, meses: 12 })) }
    catch (e) { setErr(e.message) } finally { setLoading(false) }
  }, [clientId, periodo])

  useEffect(() => { load() }, [load])

  const kpis = data?.kpis || {}
  const rent = data?.rentabilidade || {}
  const categorias = data?.despesas_categoria || { total: 0, itens: [] }
  const contas = data?.contas || []
  const alertas = data?.alertas || []
  const vencimentos = data?.proximos_vencimentos || []

  // Séries dos gráficos com o rótulo do eixo já resolvido.
  const gran = data?.granularidade || 'mes'
  const fluxo = useMemo(
    () => (data?.fluxo || []).map((f) => ({ ...f, label: rotuloCurto(f.mes) })), [data])
  const recDesp = useMemo(
    () => (data?.receitas_despesas || []).map((f) => ({ ...f, label: rotuloCurto(f.mes) })), [data])
  const serieMargem = data?.serie_margem || []
  const serieTicket = data?.serie_ticket || []

  // Intervalo que contém hoje → posição da régua "Atual" (some se o período
  // selecionado for todo passado ou todo futuro).
  const marcoAtual = useMemo(() => {
    const chave = chaveDeHoje(gran)
    return fluxo.find((f) => f.mes === chave)?.label || null
  }, [fluxo, gran])

  // Em série diária o eixo tem até 31 rótulos: deixa o recharts ralear.
  const intervaloEixo = gran === 'dia' ? 'preserveStartEnd' : 0

  // saldo pode ser legitimamente 0 ou negativo: testa presença, não verdade
  const temFluxo = fluxo.some((f) => f.realizado != null || f.projetado != null)
  const temRecDesp = recDesp.some((f) => f.receitas || f.despesas)
  const temMargem = serieMargem.some((f) => f.valor != null)
  const temTicket = serieTicket.some((f) => f.valor != null)
  const rotuloJanela = fluxo.length
    ? `${rotuloLongo(fluxo[0].mes)} – ${rotuloLongo(fluxo[fluxo.length - 1].mes)}`
    : ''

  const bc = [{ label: 'Financeiro' }]

  const topbarRight = (
    <>
      <PeriodoSelect presetId={presetId} custom={custom} onSelect={escolherPeriodo} />
      <Link className="fd-tb-btn primary" to="/financeiro/contas-receber">
        <Icon n="plus" s={15} sw={2} /> Nova receita
      </Link>
      <Link className="fd-tb-btn" to="/financeiro/contas-pagar">
        <Icon n="plus" s={15} sw={2} /> Nova despesa
      </Link>
    </>
  )

  if (!clientId) {
    return (
      <Layout title="Financeiro" breadcrumb={bc}>
        <div className="fd-state">Selecione um cliente para ver o painel financeiro.</div>
      </Layout>
    )
  }

  // Texto de cada alerta do painel Atenção (o backend manda só os números).
  const textoAlerta = (a) => {
    if (a.tipo === 'recebimentos_atrasados') {
      return {
        titulo: `${a.qtd} ${a.qtd === 1 ? 'recebimento em atraso' : 'recebimentos em atraso'}`,
        sub: `Total em aberto: ${fmtBRL(a.total)}`,
        to: '/financeiro/contas-receber',
      }
    }
    if (a.tipo === 'pagamentos_atrasados') {
      return {
        titulo: `${a.qtd} ${a.qtd === 1 ? 'pagamento em atraso' : 'pagamentos em atraso'}`,
        sub: `Total em aberto: ${fmtBRL(a.total)}`,
        to: '/financeiro/contas-pagar',
      }
    }
    return {
      titulo: `Projeção de caixa negativa em ${rotuloLongo(a.mes)}`,
      sub: `Saldo projetado: ${fmtBRL(a.saldo_projetado)}`,
      to: '/financeiro/saldo',
    }
  }

  return (
    <Layout title="Financeiro" breadcrumb={bc} topbarRight={topbarRight}>
      {err && <div className="fd-state" style={{ color: '#b42318', padding: '12px 0' }}>{err}</div>}
      {loading ? (
        <div className="fd-state">Carregando…</div>
      ) : (
        <div className="fd-wrap">

          {/* ── 1. KPIs ─────────────────────────────────────────────────── */}
          <div className="fd-kpis">
            <Kpi icon="wallet" label="Saldo disponível"
              value={brl0(kpis.saldo_disponivel)} delta={kpis.saldo_delta}
              info="Soma do saldo atual de todas as contas financeiras ativas." />
            <Kpi icon="arrow_up_circle" label="A receber"
              value={brl0(kpis.a_receber)}
              nota={`${kpis.a_receber_qtd || 0} ${kpis.a_receber_qtd === 1 ? 'título' : 'títulos'} a vencer`}
              info="Contas a receber ainda não recebidas com vencimento de hoje em diante, dentro do período selecionado. As vencidas contam como inadimplência e aparecem no painel Atenção. Mesma regra dos cards da tela Contas a Receber." />
            <Kpi icon="arrow_down_circle" label="A pagar"
              value={brl0(kpis.a_pagar)}
              nota={`${kpis.a_pagar_qtd || 0} ${kpis.a_pagar_qtd === 1 ? 'título' : 'títulos'} a vencer`}
              info="Contas a pagar ainda não pagas com vencimento de hoje em diante, dentro do período selecionado. As vencidas contam como inadimplência e aparecem no painel Atenção. Mesma regra dos cards da tela Contas a Pagar." />
            <Kpi icon="chart_up" label="Receita"
              value={brl0(kpis.receita)} delta={kpis.receita_delta}
              info="Contas a receber efetivamente recebidas no período." />
            <Kpi icon="pie" label="Margem operacional"
              value={kpis.margem_operacional == null ? '—' : pct1(kpis.margem_operacional)}
              delta={kpis.margem_delta_pp} unidade="pp"
              info="(Receitas recebidas − despesas pagas) ÷ receitas recebidas, no período." />
            <Kpi icon="card" label="Ticket médio"
              value={brl0(kpis.ticket_medio)} delta={kpis.ticket_delta}
              info={`Receita do período ÷ número de recebimentos${kpis.vendas ? ` (${kpis.vendas})` : ''}.`} />
          </div>

          {/* ── 2. Fluxo de caixa | Receitas × despesas ──────────────────── */}
          <div className="fd-row-2">
            <div className="fd-card fd-panel">
              <PanelHead title="Fluxo de caixa realizado e projetado" sub={rotuloJanela}
                info="Saldo em caixa ao fim de cada mês, partindo do saldo disponível de hoje. Até o mês corrente conta só o que foi quitado; a partir dele soma o que ainda vai vencer no período. Contas já vencidas e não quitadas não entram: elas aparecem no painel Atenção." />
              <div className="fd-legend">
                <span className="fd-legend-item"><i className="fd-legend-line" /> Realizado</span>
                <span className="fd-legend-item"><i className="fd-legend-line dashed" /> Projetado</span>
              </div>
              {temFluxo ? (
                <ResponsiveContainer width="100%" height={228}>
                  <ComposedChart data={fluxo} margin={{ top: 26, right: 8, left: 4, bottom: 0 }}>
                    <defs>
                      <linearGradient id="fdFluxoFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor={BLUE} stopOpacity={0.16} />
                        <stop offset="100%" stopColor={BLUE} stopOpacity={0.01} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis dataKey="label" tick={tickStyle} axisLine={false} tickLine={false} dy={6}
                      interval={intervaloEixo} />
                    <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={62}
                      tickFormatter={axisBRL} />
                    <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={tooltipStyle} />
                    <Area type="linear" dataKey="realizado" name="Realizado" stroke={BLUE} strokeWidth={2.2}
                      fill="url(#fdFluxoFill)" dot={false} activeDot={{ r: 4 }} connectNulls={false} />
                    <Area type="linear" dataKey="projetado" name="Projetado" stroke={BLUE} strokeWidth={2}
                      strokeDasharray="6 5" fill="url(#fdFluxoFill)" dot={false} activeDot={{ r: 4 }}
                      connectNulls={false} />
                    <ReferenceLine y={0} stroke="#344054" strokeWidth={1.2} />
                    {marcoAtual && (
                      <ReferenceLine x={marcoAtual} stroke={BLUE} strokeDasharray="4 4" label={<AtualTag />} />
                    )}
                  </ComposedChart>
                </ResponsiveContainer>
              ) : <SemDados altura={228} />}
            </div>

            <div className="fd-card fd-panel">
              <PanelHead title="Receitas x despesas" sub={rotuloJanela} />
              <div className="fd-legend">
                <span className="fd-legend-item">
                  <i className="fd-legend-sq" style={{ background: BLUE }} /> Receitas
                </span>
                <span className="fd-legend-item">
                  <i className="fd-legend-sq" style={{ background: INK }} /> Despesas
                </span>
              </div>
              {temRecDesp ? (
                <ResponsiveContainer width="100%" height={228}>
                  <BarChart data={recDesp} margin={{ top: 20, right: 8, left: 4, bottom: 0 }}
                    barGap={2} barCategoryGap="30%">
                    <CartesianGrid stroke={GRID} vertical={false} />
                    <XAxis dataKey="label" tick={tickStyle} axisLine={false} tickLine={false} dy={6}
                      interval={intervaloEixo} />
                    <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={58}
                      tickFormatter={axisBRL} />
                    <Tooltip formatter={(v) => fmtBRL(v)} cursor={{ fill: 'rgba(37,99,235,.05)' }}
                      contentStyle={tooltipStyle} />
                    <Bar dataKey="receitas" name="Receitas" fill={BLUE} barSize={9} radius={[2, 2, 0, 0]} />
                    <Bar dataKey="despesas" name="Despesas" fill={INK} barSize={9} radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <SemDados altura={228} />}
            </div>
          </div>

          {/* ── 3. Categorias | Rentabilidade | Ticket médio ─────────────── */}
          <div className="fd-row-3">
            <div className="fd-card fd-panel">
              <PanelHead title="Despesas por categoria" />
              {categorias.itens.length ? (
                <>
                  <div className="fd-donut-wrap">
                    <div className="fd-donut" style={{ position: 'relative' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={categorias.itens} dataKey="valor" nameKey="categoria"
                            cx="50%" cy="50%" innerRadius="68%" outerRadius="98%"
                            startAngle={90} endAngle={-270} paddingAngle={1} stroke="none"
                          >
                            {categorias.itens.map((c, i) => (
                              <Cell key={c.categoria} fill={CORES_CATEGORIA[i % CORES_CATEGORIA.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={tooltipStyle} />
                        </PieChart>
                      </ResponsiveContainer>
                      {/* cabe dentro do furo do donut (≈104px) sem encostar no anel */}
                      <div className="fd-donut-center">
                        <div className="fd-donut-total">{brl0(categorias.total)}</div>
                        <div className="fd-donut-label">Total</div>
                      </div>
                    </div>

                    <div className="fd-cat-list">
                      {categorias.itens.map((c, i) => (
                        <div className="fd-cat-row" key={c.categoria}>
                          <span className="fd-cat-name" title={c.categoria}>
                            <i style={{ background: CORES_CATEGORIA[i % CORES_CATEGORIA.length] }} />
                            {c.categoria}
                          </span>
                          <span className="fd-cat-pct">{pct1(c.pct)}</span>
                          <span className="fd-cat-val">{brl0(c.valor)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="fd-foot-note">Despesas no período</div>
                </>
              ) : <SemDados altura={190} texto="Nenhuma despesa no período." />}
            </div>

            <div className="fd-card fd-panel">
              <PanelHead title="Rentabilidade"
                info="Receita recebida, custos pagos e o resultado entre os dois no período." />
              <div className="fd-rent">
                <div>
                  <div className="fd-rent-row">
                    <span className="fd-rent-label">Receita</span>
                    <span className="fd-rent-value">{fmtBRL(rent.receita || 0)}</span>
                    <Delta valor={rent.receita_delta} suffix="" />
                  </div>
                  <div className="fd-rent-row">
                    <span className="fd-rent-label">Custos</span>
                    <span className="fd-rent-value">{fmtBRL(rent.custos || 0)}</span>
                    <Delta valor={rent.custos_delta} suffix="" bomQuandoSobe={false} />
                  </div>
                  <div className="fd-rent-sep" />
                  <div className="fd-rent-row">
                    <span className="fd-rent-label">Resultado<br />operacional</span>
                    <span className="fd-rent-value accent">{fmtBRL(rent.resultado || 0)}</span>
                    <Delta valor={rent.resultado_delta} suffix="" />
                  </div>
                  <div className="fd-rent-row">
                    <span className="fd-rent-label muted">Margem operacional</span>
                    <span className="fd-rent-value" style={{ fontWeight: 500 }}>
                      {rent.margem == null ? '—' : pct1(rent.margem)}
                    </span>
                    <Delta valor={rent.margem_delta_pp} unidade="pp" suffix="" />
                  </div>
                </div>

                <div>
                  {temMargem ? (
                    <>
                      <ResponsiveContainer width="100%" height={150}>
                        <LineChart data={serieMargem} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                          <CartesianGrid stroke={GRID} vertical={false} />
                          <XAxis dataKey="mes" hide />
                          <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={38}
                            tickFormatter={axisPct} />
                          <Tooltip formatter={(v) => pct1(v)} labelFormatter={rotuloLongo}
                            contentStyle={tooltipStyle} />
                          <Line type="monotone" dataKey="valor" name="Margem" stroke={BLUE}
                            strokeWidth={2} dot={false} connectNulls />
                        </LineChart>
                      </ResponsiveContainer>
                      <div className="fd-axis-ends" style={{ paddingLeft: 38 }}>
                        <span>{rotuloLongo(serieMargem[0].mes)}</span>
                        <span>{rotuloLongo(serieMargem[serieMargem.length - 1].mes)}</span>
                      </div>
                    </>
                  ) : <SemDados altura={150} texto="Sem margem no período." />}
                </div>
              </div>
            </div>

            <div className="fd-card fd-panel">
              <PanelHead title="Ticket médio" sub={rotuloJanela && `(${rotuloJanela})`} />
              <div className="fd-ticket-value">{brl0(kpis.ticket_medio)}</div>
              <Delta valor={kpis.ticket_delta} />
              <div style={{ marginTop: 18 }}>
                {temTicket ? (
                  <>
                    <ResponsiveContainer width="100%" height={110}>
                      <LineChart data={serieTicket} margin={{ top: 6, right: 6, left: 0, bottom: 0 }}>
                        <CartesianGrid stroke={GRID} vertical={false} />
                        <XAxis dataKey="mes" hide />
                        <YAxis tick={tickStyle} axisLine={false} tickLine={false} width={44}
                          tickFormatter={axisBRL} />
                        <Tooltip formatter={(v) => fmtBRL(v)} labelFormatter={rotuloLongo}
                          contentStyle={tooltipStyle} />
                        <Line type="monotone" dataKey="valor" name="Ticket médio" stroke={BLUE}
                          strokeWidth={2} dot={false} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                    <div className="fd-axis-ends" style={{ paddingLeft: 44 }}>
                      <span>{rotuloLongo(serieTicket[0].mes)}</span>
                      <span>{rotuloLongo(serieTicket[serieTicket.length - 1].mes)}</span>
                    </div>
                  </>
                ) : <SemDados altura={110} texto="Nenhum recebimento no período." />}
              </div>
            </div>
          </div>

          {/* ── 4. Vencimentos | Contas bancárias | Atenção ──────────────── */}
          <div className="fd-row-4">
            <div className="fd-card fd-panel">
              <PanelHead title="Próximos vencimentos" />
              {vencimentos.length ? (
                <div className="fd-table-scroll">
                  <table className="fd-table">
                    <thead>
                      <tr>
                        <th>Data</th><th>Descrição</th><th>Tipo</th>
                        <th>Categoria</th><th>Valor</th><th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vencimentos.map((r) => (
                        <tr key={r.id}>
                          <td>{fmtDate(r.data_vencimento)}</td>
                          <td className="fd-desc fd-clip" title={primeiraLinha(r.descricao)}>
                            {primeiraLinha(r.descricao)}
                          </td>
                          <td>
                            <span className={`fd-pill ${r.tipo === 'receber' ? 'receber' : 'pagar'}`}>
                              {r.tipo === 'receber' ? 'A receber' : 'A pagar'}
                            </span>
                          </td>
                          <td className="fd-clip" title={r.categoria || ''}>{r.categoria || '—'}</td>
                          <td className="fd-num">{fmtBRL(r.valor)}</td>
                          <td>
                            <span className="fd-pill vencer">
                              {daysUntil(r.data_vencimento) === 0 ? 'Vence hoje' : 'A vencer'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : <SemDados altura={160} texto="Nenhuma conta a vencer." />}
              <Link className="fd-link" to="/financeiro/contas-pagar">
                Ver todas as contas a pagar e receber <Icon n="arrow_right" s={14} sw={2} />
              </Link>
            </div>

            <div className="fd-card fd-panel">
              <PanelHead title="Saldos em contas bancárias" />
              {contas.length ? (
                <div className="fd-accounts">
                  {contas.slice(0, 3).map((c) => (
                    <div className="fd-account" key={c.id}>
                      <div className="fd-account-head">
                        <AccountLogo nome={c.nome} />
                        <span className="fd-account-name" title={c.nome}>{c.nome}</span>
                      </div>
                      <div className="fd-account-label">Saldo atual</div>
                      <div className="fd-account-value">{fmtBRL(c.saldo_atual)}</div>
                      <div className="fd-account-tipo">{TIPO_SALDO_LABEL[c.tipo] || c.tipo}</div>
                      <Link className="fd-link" to="/financeiro/saldo">Ver extrato</Link>
                    </div>
                  ))}
                </div>
              ) : (
                <SemDados altura={160} texto="Nenhuma conta financeira cadastrada." />
              )}
              {contas.length > 3 && (
                <Link className="fd-link" to="/financeiro/saldo">
                  Ver todas as {contas.length} contas <Icon n="arrow_right" s={14} sw={2} />
                </Link>
              )}
            </div>

            <div className={`fd-card fd-panel${alertas.length ? ' fd-alert-card' : ''}`}>
              <div className={`fd-alert-title${alertas.length ? '' : ' ok'}`}>
                <Icon n={alertas.length ? 'alert' : 'check'} s={16} sw={1.8} /> Atenção
              </div>
              {alertas.length ? alertas.map((a) => {
                const t = textoAlerta(a)
                return (
                  <Link className="fd-alert-item" key={a.tipo} to={t.to}>
                    <span className="fd-alert-icon"><Icon n="clock" s={16} /></span>
                    <span className="fd-alert-text">
                      <span className="fd-alert-head">{t.titulo}</span>
                      <span className="fd-alert-sub">{t.sub}</span>
                    </span>
                    <Icon n="chevron_r" s={15} c="#98a2b3" />
                  </Link>
                )
              }) : (
                <SemDados altura={120} texto="Nenhuma pendência: sem atrasos e sem projeção de caixa negativa." />
              )}
              <Link className="fd-link" to="/financeiro/saldo">
                Ver relatório de fluxo de caixa <Icon n="arrow_right" s={14} sw={2} />
              </Link>
            </div>
          </div>
        </div>
      )}
    </Layout>
  )
}
